/**
 * Tipos TS espelhando os schemas Pydantic do backend
 * (app/modules/whatsapp/schemas.py). Mantém sincronizado manualmente.
 */

export type WhatsAppItemCategoria = "disponibilidade" | "negociacao" | "sentimento";
export type WhatsAppItemStatus = "pendente" | "aprovado" | "rejeitado" | "redundante";
export type WhatsAppRunStatus = "running" | "done" | "error";
export type WhatsAppConfidence = "alta" | "media" | "baixa";

export interface WhatsAppRunStats {
  conversas_na_janela?: number;
  conversas_processadas?: number;
  conversas_puladas_grupo?: number;
  items?: Record<string, number>;
  tokens?: { input: number; output: number };
  custo_estimado_cents?: number;
  erros_extracao?: { conversation_id: string; error: string }[];
  atendimento?: {
    tempo_primeira_resposta_mediana_min: number | null;
    tempo_primeira_resposta_p90_min: number | null;
    conversas_sem_resposta: number;
    mensagens: { contato: number; atendente: number; ia: number };
    por_conversa: {
      conversation_id: string;
      contact_name: string | null;
      telefone: string | null;
      primeira_resposta_min: number | null;
      sem_resposta: boolean;
      msgs_contato: number;
      msgs_atendente: number;
      msgs_ia: number;
    }[];
  };
}

/** Contadores por categoria: {categoria: {review_status: n}} */
export type WhatsAppItemCounts = Partial<
  Record<WhatsAppItemCategoria, Partial<Record<WhatsAppItemStatus, number>>>
>;

export interface WhatsAppRun {
  id: string;
  window_start: string;
  window_end: string;
  started_at: string;
  finished_at: string | null;
  status: WhatsAppRunStatus;
  error_message: string | null;
  stats: WhatsAppRunStats;
  created_by: string | null;
  item_counts: WhatsAppItemCounts;
}

export interface WhatsAppItemQuote {
  autor: string;
  texto: string;
  message_id: string | null;
}

export interface WhatsAppDisponibilidadePayload {
  dimension: "interesse_freela" | "situacao_emprego" | "relacao_conosco";
  value: string;
}

export interface WhatsAppNegociacaoPayload {
  status_sugerido: string | null;
  valor_centavos: number | null;
  eh_pretensao: boolean;
  periodo: string | null;
  vaga_mencionada: string | null;
  vaga_sugerida_id?: string | null;
  vaga_sugerida_label?: string | null;
  data_disponibilidade: string | null;
  contraparte: "freelancer" | "agencia" | "fornecedor";
  contato_tipo?: "freelancer" | "fornecedor" | "agencia" | "outro";
}

export interface WhatsAppSentimentoPayload {
  tom: "positivo" | "neutro" | "negativo" | "critico";
  conversa_critica: boolean;
  facilidade_relacionamento: "facil" | "neutro" | "dificil" | null;
  motivo_insatisfacao: string | null;
}

export type WhatsAppMotivoDescarte =
  | "nao_e_verdade"
  | "evidencia_fraca"
  | "contato_errado"
  | "nao_e_freelancer"
  | "irrelevante"
  | "ja_sabiamos"
  | "outro";

/** Taxonomia fechada: o motivo alimenta as lições que voltam pro prompt. */
export const MOTIVOS_DESCARTE: { valor: WhatsAppMotivoDescarte; label: string }[] = [
  { valor: "nao_e_verdade", label: "Não é verdade" },
  { valor: "evidencia_fraca", label: "Evidência fraca" },
  { valor: "nao_e_freelancer", label: "Não é freelancer" },
  { valor: "contato_errado", label: "Contato errado" },
  { valor: "ja_sabiamos", label: "Já sabíamos" },
  { valor: "irrelevante", label: "Irrelevante" },
  { valor: "outro", label: "Outro motivo" },
];

export interface WhatsAppItem {
  id: string;
  run_id: string;
  categoria: WhatsAppItemCategoria;
  conversation_id: string;
  freelancer_id: string | null;
  freelancer_nome: string | null;
  telefone: string | null;
  contact_name: string | null;
  observed_at: string;
  payload:
    | WhatsAppDisponibilidadePayload
    | WhatsAppNegociacaoPayload
    | WhatsAppSentimentoPayload
    | Record<string, unknown>;
  resumo: string;
  quotes: WhatsAppItemQuote[];
  message_ids: string[];
  confidence: WhatsAppConfidence;
  review_status: WhatsAppItemStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  applied_at: string | null;
  applied_ref: Record<string, unknown> | null;
  created_at: string;
  review_reason?: WhatsAppMotivoDescarte | null;
  review_note?: string | null;
  payload_corrigido?: Record<string, unknown> | null;
  prompt_version?: string | null;
}

export interface WhatsAppResumo {
  pendentes: number;
  aprovados: number;
  descartados: number;
  redundantes: number;
  ultima_run: {
    id: string;
    status: WhatsAppRunStatus;
    finished_at: string | null;
    window_end: string;
    conversas_processadas: number | null;
  } | null;
  ultima_mensagem_sincronizada: string | null;
  contatos_ignorados: number;
}

/** Um ponto da série histórica (uma rodada concluída). */
export interface WhatsAppMetricaPonto {
  run_id: string;
  numero: number;
  window_start: string;
  window_end: string;
  finished_at: string | null;
  conversas: number;
  mediana_primeira_resposta_min: number | null;
  p90_primeira_resposta_min: number | null;
  conversas_sem_resposta: number;
  msgs_contato: number;
  msgs_atendente: number;
  msgs_ia: number;
  disponibilidade: number;
  negociacao: number;
  sentimento: number;
  custo_estimado_cents: number;
}

export interface WhatsAppConversaMetrica {
  conversation_id: string;
  contact_name: string | null;
  telefone: string | null;
  primeira_resposta_min: number | null;
  sem_resposta: boolean;
  msgs_contato: number;
  msgs_atendente: number;
  msgs_ia: number;
}

export interface WhatsAppMetricas {
  series: WhatsAppMetricaPonto[];
  por_conversa_ultima: WhatsAppConversaMetrica[];
}

export interface WhatsAppContatoIgnorado {
  id: string;
  contact_name: string | null;
  phone_number: string | null;
  freelancer_nome: string | null;
  ignore_reason: string | null;
  ignored_at: string;
  last_message_at: string | null;
}

export interface WhatsAppTranscriptMessage {
  id: string;
  autor: string;
  texto: string;
  message_type: string;
  created_at: string;
}

export interface WhatsAppTranscript {
  conversation_id: string;
  contact_name: string | null;
  telefone: string | null;
  freelancer_id: string | null;
  messages: WhatsAppTranscriptMessage[];
}

export const CATEGORIA_LABELS: Record<WhatsAppItemCategoria, string> = {
  disponibilidade: "Disponibilidade",
  negociacao: "Negociação",
  sentimento: "Sentimento",
};

export const ITEM_STATUS_LABELS: Record<WhatsAppItemStatus, string> = {
  pendente: "Pendente",
  aprovado: "Aprovado",
  rejeitado: "Descartado",
  redundante: "Já confirmado",
};

export const DIMENSION_LABELS: Record<string, string> = {
  interesse_freela: "Interesse em freela",
  situacao_emprego: "Situação de emprego",
  relacao_conosco: "Relação conosco",
};

export const DIMENSION_VALUE_LABELS: Record<string, string> = {
  topa_freela: "Topa freela",
  aberto_mas_ocupado: "Aberto, mas ocupado",
  nao_topa: "Não topa",
  empregado_fixo: "Empregado fixo",
  freela_ativo: "Freela ativo",
  entre_trabalhos: "Entre trabalhos",
  trabalhando_conosco: "Trabalhando conosco",
  disponivel_em_breve: "Disponível em breve",
  ja_trabalhou_conosco: "Já trabalhou conosco",
  nunca_trabalhou: "Nunca trabalhou",
  desconhecido: "A confirmar",
};

/** Vocabulário por eixo (espelha chk_value_por_dimensao da M135): permite ao
 * revisor corrigir o valor antes de aprovar. */
export const DIMENSION_VALUES: Record<string, string[]> = {
  interesse_freela: ["topa_freela", "aberto_mas_ocupado", "nao_topa"],
  situacao_emprego: ["empregado_fixo", "freela_ativo", "entre_trabalhos"],
  relacao_conosco: [
    "trabalhando_conosco",
    "disponivel_em_breve",
    "ja_trabalhou_conosco",
    "nunca_trabalhou",
  ],
};

export const NEGOCIACAO_STATUS_LABELS: Record<string, string> = {
  oferta_feita: "Oferta feita",
  topou: "Topou",
  contra_proposta: "Contraproposta",
  contra_proposta_aceita: "Contraproposta aceita",
  aguardando_resposta_agencia: "Aguardando resposta da agência",
  recusou: "Recusou",
  desistiu: "Desistiu",
};

export const TOM_LABELS: Record<string, string> = {
  positivo: "Positivo",
  neutro: "Neutro",
  negativo: "Negativo",
  critico: "Crítico",
};

/** Superfície onde a conferência aconteceu (mede se a captura fora da fila
 * funcionou). Espelha o CHECK de whatsapp_item_feedback (M185). */
export type WhatsAppSurface = "fila" | "ficha_freelancer" | "timeline" | "kanban";

export interface WhatsAppTrechoMensagem extends WhatsAppTranscriptMessage {
  /** Mensagem que sustenta a conclusão da IA. */
  ancora: boolean;
}

export interface WhatsAppTrecho {
  item_id: string;
  conversation_id: string;
  contact_name: string | null;
  telefone: string | null;
  mensagens: WhatsAppTrechoMensagem[];
  total_na_conversa: number;
  /** Falso para quem tem a ficha do freelancer mas não a tela do WhatsApp. */
  pode_ver_conversa_completa: boolean;
}

export interface WhatsAppSugestoes {
  freelancer_id: string;
  pendentes: { item: WhatsAppItem; ja_vigente: boolean }[];
  aplicados_recentes: WhatsAppItem[];
}

/** De quem é a próxima mensagem, segundo o resumo. */
export type WhatsAppAguardando = "contato" | "equipe" | "ninguem";

export interface WhatsAppDiaDoResumo {
  /** YYYY-MM-DD */
  data: string;
  texto: string;
}

/** Leitura da IA sobre a conversa; não é fato e não gera sinal (COR-387). */
export interface WhatsAppHipotese {
  disponibilidade: {
    interesse_freela: string | null;
    situacao_emprego: string | null;
    confianca: WhatsAppConfidence;
    base: string | null;
  } | null;
  negociacao: {
    status: string;
    valor_centavos: number | null;
    vaga: string | null;
    confianca: WhatsAppConfidence;
    base: string | null;
  } | null;
}

export type WhatsAppOrigemResumo = "rolante" | "regerado" | "humano";

/** Resumo corrido vigente de uma conversa (COR-382); não passa por aprovação. */
export interface WhatsAppResumoConversa {
  conversation_id: string;
  /** Id da versão vigente do resumo. */
  resumo_id: string;
  /** rolante | regerado (IA) ou humano (corrigido por uma pessoa). */
  origem: WhatsAppOrigemResumo;
  conferido_em: string | null;
  conferido_por: string | null;
  versoes: number;
  contact_name: string | null;
  telefone: string | null;
  resumo: string;
  /** Um dia por entrada (COR-387); null em resumo antigo, que a tela mostra como prosa. */
  historico: WhatsAppDiaDoResumo[] | null;
  ultimo_status: string | null;
  aguardando: WhatsAppAguardando | null;
  gerado_em: string;
  /** Até onde o resumo leu: o "de quando" da informação. */
  mensagens_ate: string;
  ultima_mensagem_em: string | null;
  ultima_mensagem_contato_em: string | null;
  /** Falso para quem tem a ficha do freelancer mas não a tela do WhatsApp. */
  pode_ver_conversa: boolean;
}

export interface WhatsAppResumoFreelancer {
  freelancer_id: string;
  conversas: WhatsAppResumoConversa[];
}

/** Linha da aba Resumos: o resumo vigente da conversa com o freelancer ao lado. */
export interface WhatsAppResumoListado extends WhatsAppResumoConversa {
  freelancer_id: string | null;
  freelancer_nome: string | null;
  hipotese: WhatsAppHipotese | null;
  /** Vigente por eixo (interesse_freela, situacao_emprego, relacao_conosco). */
  disponibilidade_atual: Record<string, string>;
}

export interface WhatsAppMensagemEncontrada {
  message_id: string;
  conversation_id: string;
  autor: string;
  texto: string;
  created_at: string;
  contact_name: string | null;
  telefone: string | null;
  freelancer_id: string | null;
  freelancer_nome: string | null;
}

export interface WhatsAppBuscaMensagens {
  mensagens: WhatsAppMensagemEncontrada[];
  limite_atingido: boolean;
}

export interface WhatsAppResumoLista {
  resumos: WhatsAppResumoListado[];
  total: number;
  por_aguardando: Record<string, number>;
}

export type WhatsAppSurfaceResumo = "ficha_freelancer" | "aba_resumos";

/** Uma versão do resumo da conversa, para a linha do tempo de versões. */
export interface WhatsAppVersaoResumo {
  id: string;
  gerado_em: string;
  origem: WhatsAppOrigemResumo;
  prompt_version: string | null;
  resumo: string;
  historico: WhatsAppDiaDoResumo[] | null;
  ultimo_status: string | null;
  aguardando: WhatsAppAguardando | null;
  mensagens_ate: string;
  autor_nome: string | null;
  conferido_em: string | null;
  conferido_por: string | null;
}
